"use client";

import { Core } from "@faire/design-tokens";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Add } from "@faire/web/slate/icons/foundation/Add";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Close } from "@faire/web/slate/icons/foundation/Close";
import { FormInputLabel, Input } from "@faire/web/slate/Input";
import {
  getDerivedPropsFromErrorProps,
  IErrorProps,
} from "@faire/web/slate/Input/error";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { useUniqueId } from "@faire/web/ui/hooks/useUniqueId";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import { useTheme } from "styled-components";

import { DROPDOWN_LIST_HEIGHT, ROW_SIZE } from "./constants";
import {
  CloseWrapper,
  CreateNewOptionDropdownItem,
  CustomEditSpan,
  DropdownItem,
  DropdownWrapper,
  EditOptionButton,
  MobileChevronWrapper,
  MobileInnerWrapper,
  MobileSelectWrapper,
  OptionRow,
  StyledSelect,
  Wrapper,
} from "./styles";

export interface IOption<T extends string = string> {
  label: string;
  value: T;
  disabled?: boolean;
}

export namespace IOption {
  export const build = (partial?: Partial<IOption>): IOption => ({
    label: "",
    value: "",
    ...(partial ?? {}),
  });
}

interface ICustomOption<T extends string = string> extends IOption<T> {
  onValueChange: (value: string) => void;
  hasEdited: boolean;
  placeholder?: string;
  onClick?: () => void;
}

export interface ICreateNewOption {
  label:
    | React.ReactNode
    | ((searchValue: string | undefined) => React.ReactNode);
  onClick?: (searchValue: string | undefined) => void;
  closeDropdown: boolean;
  hideUnlessNoOptions?: boolean;
}

type IOptionMixin<T extends string = string> = IOption<T> | ICustomOption<T>;

export interface IProps<T extends string = string> extends IErrorProps {
  className?: string;
  /** The currently selected dropdown value. */
  value?: T;
  /** Text for the label above the dropdown menu. */
  label?: string;
  /** Text to display when no value is selected. */
  placeholder?: HTMLInputElement["placeholder"];
  /** Text displayed beneath the dropdown. Will not display if an error message is provided. */
  helper?: string;
  /** Options provided in the dropdown. Each option contains a `label` and a `value` string. */
  options: IOption<T>[];
  /** handles when an option is selected from the dropdown. */
  onSelect: (option: IOption<T>) => void;
  /** handles when user clears the selected option **/
  onClear?: () => void;
  /** handles when the dropdown is opened. Note: on mweb, does not get called if the dropdown is opened via the arrow keys due to a limitation. */
  onDropdownOpen?: () => void;
  onDropdownClose?: () => void;
  /** Disables the dropdown and applies the `disabled` styles */
  disabled?: boolean;
  "data-test-id"?: string;
  /** What, if any, permission the user agent has to provide automated assistance in filling out dropdown values, as well as guidance to the browser as to the type of information expected in the field. */
  autoComplete?: string;
  /** id passed to the html input */
  id?: string;
  /** This prop disable type in search option. Only allow users to select options in dropdown menu.*/
  disableInput?: boolean;
  /** The element for the container that the dropdown should try to stay within
     will likely come from react modal. */
  containingElement?: HTMLElement;
  /** Hides the border of the input when it is not being focussed */
  borderless?: boolean;
  /** Filters results when searched */
  filter?: boolean;
  /** Allows for specifying a custom option that will appear at the end of the options list*/
  customOption?: ICustomOption<T>;
  "aria-label"?: string;
  /** Allows create new option as an option in dropdown menu*/
  createNewOption?: ICreateNewOption;
  /** Allows getting a ref to the input component instance. */
  inputRef?: React.RefObject<HTMLInputElement>;
  /** Allows for data to be visible in hotjar recordings */
  "data-hj-allow"?: boolean;
  /** Uses the tablet and above dropdown for mobile viewports */
  useTabletAndAboveDropdownStyle?: boolean;
}
const isCustomOption = (
  maybeCustomOption: IOption | ICustomOption
): maybeCustomOption is ICustomOption => {
  return "onValueChange" in maybeCustomOption;
};

const getLabel = <T extends string = string>(option: IOption<T>) =>
  option.label;
const getValue = <T extends string = string>(option: IOption<T>): T =>
  option.value;

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/dropdown)
 */
// eslint-disable-next-line complexity
export const Dropdown = <T extends string = string>(props: IProps<T>) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    options,
    filter,
    onSelect,
    value,
    containingElement,
    disableInput,
    className,
    placeholder,
    label,
    error,
    helper,
    disabled,
    autoComplete = "off",
    id,
    inputRef,
    borderless,
    customOption,
    "aria-label": ariaLabel,
    "data-test-id": dataTestId,
    "data-hj-allow": dataHjAllow,
    useTabletAndAboveDropdownStyle,
    onDropdownOpen,
    onDropdownClose,
  } = props;
  const theme = useTheme();
  const viewport = useViewport();
  const defaultId = useUniqueId("input-");
  const dropdownId = id ?? defaultId;
  let targetRef = React.useRef<HTMLInputElement>(null);
  if (inputRef) {
    targetRef = inputRef;
  }

  const refs = React.useRef<Record<number, HTMLButtonElement | null>>({});
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState<string | undefined>(
    undefined
  );
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const getModularIndex = (index: number, maxValue: number) =>
    ((index % maxValue) + maxValue) % maxValue;

  const [isCustomizingOption, setIsCustomizingOption] = React.useState(false);

  const filteredOptions = React.useMemo((): IOptionMixin<T>[] => {
    if ((!searchValue || !filter) && customOption === undefined) {
      return options;
    }

    const containsSearch = (option: IOption<T>) =>
      getLabel(option)
        .toLowerCase()
        .indexOf(searchValue?.toLowerCase() ?? "") !== -1;
    const filtered = options.filter(containsSearch);
    if (customOption !== undefined && customOption.value !== undefined) {
      // 2 cases:
      //  1. the search value matches the custom value
      //  2. user hasn't constructed the custom value yet
      // in both cases we want maintain it in the dropdown
      if (
        customOption.value.indexOf(searchValue?.toLowerCase() ?? "") !== -1 ||
        !customOption.hasEdited
      ) {
        filtered.push(customOption);
      }
    }
    return filtered;
  }, [searchValue, filter, customOption, options]);

  const handleInputOnChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    if (!isCustomizingOption) {
      handleDropdownOpen();

      const search = event.target.value;
      setSearchValue(search);

      if (search === "") {
        setHighlightIndex(0);
        scrollIntoView(0);
        return;
      }

      const optionIndex = filteredOptions.findIndex((e) =>
        getLabel(e).toLowerCase().startsWith(search.toLowerCase())
      );
      if (optionIndex !== -1) {
        setHighlightIndex(optionIndex);
        scrollIntoView(optionIndex);
      }
    } else {
      setSearchValue(event.target.value);
    }
  };

  const enterCustomValue = () => {
    if (customOption && searchValue) {
      customOption.onValueChange(searchValue);
      setIsCustomizingOption(false);
      setSearchValue(undefined);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      if (isCustomizingOption) {
        enterCustomValue();
      } else if (props.createNewOption && filteredOptions.length === 0) {
        setSearchValue(undefined);
        if (props.createNewOption.closeDropdown) {
          setOpen(false);
        }
        props.createNewOption?.onClick?.(searchValue);
      }
    }
  };

  const handleClickOutside = () => {
    if (isCustomizingOption) {
      enterCustomValue();
    } else {
      handleDropdownClose();
    }
  };

  const handleSelectDropDown = (optionValue: T) => {
    let option = undefined;
    if (customOption && getValue(customOption) === optionValue) {
      option = customOption;
    } else {
      option = options.find((e) => getValue(e) === optionValue);
    }
    onSelect(option!);
    setOpen(false);
    setSearchValue(undefined);
  };

  const handleDropdownOpen = () => {
    onDropdownOpen?.();
    setOpen(true);
    if (!value) {
      return;
    }

    const selectedIndex =
      customOption && customOption.value === value
        ? options.length
        : options.findIndex((o) => o.value === value);

    if (selectedIndex !== -1) {
      setHighlightIndex(selectedIndex);
    }
  };

  const handleDropdownClose = () => {
    onDropdownClose?.();
    setSearchValue(undefined);
    setOpen(false);
  };

  const handleSelectDropDownItem = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    event.preventDefault();
    select(index);
  };

  const handleEditCustomOption = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setIsCustomizingOption(true);

    handleDropdownClose();
    if (targetRef.current) {
      targetRef.current.focus();
    }

    setSearchValue(customOption?.hasEdited ? customOption.value : "");
  };

  const select = (selectIndex: number) => {
    const selectedOption = filteredOptions[selectIndex];
    const value = selectedOption ? getValue(selectedOption) : undefined;

    if (value !== undefined) {
      handleSelectDropDown(value);
    }
  };

  const scrollIntoView = (index: number) => {
    const ref = refs.current[index];
    if (!ref) {
      return;
    }
    ref.scrollIntoView?.({
      block: "nearest",
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (["Tab", "Escape"].includes(event.key)) {
      handleDropdownClose();
      return;
    }

    const isRelevantKey = ["ArrowDown", "ArrowUp", "Enter"].includes(event.key);
    if (!isRelevantKey) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      let nextIndex = highlightIndex;
      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp":
          nextIndex = getNextIndex(event.key);
          break;
        case "Enter":
          select(highlightIndex);
          break;
        default:
          break;
      }

      scrollIntoView(nextIndex);
      setHighlightIndex(nextIndex);
    } else {
      handleDropdownOpen();
    }
  };

  /**
   * get the next non-disabled index
   */
  const getNextIndex = (eventKey: "ArrowDown" | "ArrowUp"): number => {
    const optionsCount = filteredOptions.length;
    let nextIndex = highlightIndex;

    for (let i = 0; i < optionsCount; ++i) {
      nextIndex = nextIndex + (eventKey === "ArrowDown" ? 1 : -1);
      nextIndex = getModularIndex(nextIndex, optionsCount);

      const nextOption = filteredOptions[nextIndex];
      if (nextOption && !nextOption.disabled) {
        return nextIndex;
      }
    }
    // we've iterated over all possibilities, let's keep the same option
    return highlightIndex;
  };

  const onHighlight = (index: number) => () => {
    setHighlightIndex(index);
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (targetRef.current) {
      targetRef.current.setSelectionRange(0, targetRef.current.value.length);
    }
    if (open) {
      handleDropdownClose();
    } else {
      handleDropdownOpen();
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const currentSelectedLabel = filteredOptions.find(
    (o) => o.value === value
  )?.label;

  // Returns true if the dropdown should open up, false otherwise.
  const calculateIsUp = () => {
    const sizeOfDropdown =
      ROW_SIZE * Math.min(DROPDOWN_LIST_HEIGHT / ROW_SIZE, options.length);
    const potentialBottom = targetRef.current?.getBoundingClientRect().top;
    const potentialTop = targetRef.current?.getBoundingClientRect().bottom;

    if (potentialBottom === undefined || potentialTop === undefined) {
      return false;
    }

    // If we're going to go above the screen, it's better to go down
    if (potentialBottom - sizeOfDropdown < 0) {
      return false;
    }

    return (
      potentialTop + sizeOfDropdown >
      (containingElement?.getBoundingClientRect()?.bottom ??
        getWindow()?.innerHeight ??
        0)
    );
  };

  const displayValue = disableInput
    ? currentSelectedLabel ?? ""
    : searchValue ?? currentSelectedLabel ?? "";

  const { showErrorState, showErrorMessage, errorMessage } =
    getDerivedPropsFromErrorProps({ error });

  const labelId = label ? `${dropdownId}__label` : undefined;

  const ariaProps = React.useMemo(() => {
    if (ariaLabel) {
      return { "aria-label": ariaLabel };
    }
    if (labelId) {
      return { "aria-labelledby": labelId };
    }
    return {};
  }, [ariaLabel, labelId]);

  if (viewport.isMobile && !useTabletAndAboveDropdownStyle) {
    const isValueInOptions = filteredOptions.some(
      (option) => option.value === value
    );
    const validValue = isValueInOptions ? value : "";
    return (
      <MobileSelectWrapper className={className}>
        {isNotUndefined(label) ? (
          <FormInputLabel
            $typography={{
              ...(theme?.input.label ?? {}),
              color: showErrorState
                ? "error"
                : theme?.input.label?.color ?? Core.text.default,
            }}
          >
            {label}
          </FormInputLabel>
        ) : null}
        <MobileInnerWrapper
          $borderless={borderless}
          $showErrorState={showErrorState}
          $disabled={disabled}
        >
          <MobileChevronWrapper>
            <Chevron
              direction="down"
              color={disabled ? Core.icon.disabled : Core.icon.default}
              width="8"
              height="8"
              strokeWidth={2}
            />
          </MobileChevronWrapper>
          <StyledSelect
            id={dropdownId}
            data-test-id={dataTestId}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore used for tests, even though it's not a valid prop on select in some React versions.
            placeholder={placeholder}
            disabled={disabled}
            onClick={(e) => {
              onDropdownOpen?.();
              e.preventDefault();
              e.stopPropagation();
            }}
            onChange={(e) => handleSelectDropDown(e.target.value as T)}
            value={validValue || ""}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {filteredOptions.map((option) => (
              <option
                data-test-id={`dropdownItem_${option.value}`}
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </StyledSelect>
        </MobileInnerWrapper>

        {helper && !showErrorMessage ? (
          <>
            <Spacer size={4} />
            <Typography
              {...{
                color: "pewter", // TODO: map new color https://fairewholesale.atlassian.net/browse/FD-127193
                ...(theme?.input.helper ?? {}),
              }}
            >
              {helper}
            </Typography>
          </>
        ) : null}
        {showErrorMessage ? (
          <>
            <Spacer size={4} />
            <Typography variant="labelSansRegular" color={Core.text.critical}>
              {errorMessage}
            </Typography>
          </>
        ) : null}
      </MobileSelectWrapper>
    );
  }

  const renderCreateNewOptionButton = () => {
    if (!props.createNewOption) {
      return null;
    }

    if (
      props.createNewOption.hideUnlessNoOptions &&
      (!searchValue ||
        filteredOptions.some(
          (option) =>
            option.label.toLocaleLowerCase() ===
            searchValue.toLocaleLowerCase().trim()
        ))
    ) {
      return null;
    }

    const index = filteredOptions.length + 1;
    const createNewOptionLabel =
      typeof props.createNewOption.label === "function"
        ? props.createNewOption.label(searchValue)
        : props.createNewOption.label;

    return (
      <CreateNewOptionDropdownItem
        key="dropdownItem_CustomizedOption"
        role="option"
        tabIndex={-1}
        onClick={() => {
          if (props.createNewOption?.closeDropdown) {
            setOpen(false);
          }

          if (props.createNewOption?.hideUnlessNoOptions) {
            setSearchValue(undefined);
          }

          props.createNewOption?.onClick?.(searchValue);
        }}
        data-test-id="dropdownItem_CustomizedOption"
        type="button"
        ref={(el: HTMLButtonElement | null) => {
          if (!el) {
            return;
          }
          refs.current[index] = el;
        }}
        onMouseOver={onHighlight(index)}
        $highlight={index === highlightIndex}
      >
        <Row align="center">
          <Add
            color={Core.icon.default}
            height={getSpacing("2x")}
            width={getSpacing("2x")}
            aria-label={strictLocalize({
              defaultMessage: "Add new option value",
              description: {
                text: "Message for screen readers when selecting a customized option value",
              },
            })}
          />
          <Spacer size="1x" />
          {createNewOptionLabel}
        </Row>
      </CreateNewOptionDropdownItem>
    );
  };

  const renderSuffixNode = () => {
    if (value && props.onClear) {
      return null;
    }
    return (
      <Chevron
        direction="down"
        width="8"
        height="8"
        color={disabled ? Core.icon.disabled : Core.icon.default}
        strokeWidth={2}
        margin={`0 0 0 ${getSpacing("1x")}`}
      />
    );
  };

  const renderClearButton = () => {
    if (value && props.onClear) {
      return (
        <CloseWrapper
          onClick={props.onClear}
          $hasHelperText={!!helper}
          data-test-id="option-clear-button"
          type="button"
        >
          <Close
            color={Core.icon.subdued}
            titleAccess={strictLocalize({
              defaultMessage: "Clear selection",
              description: {
                text: "Description of icon to clear the selection for screen readers",
              },
            })}
          />
        </CloseWrapper>
      );
    }
    return null;
  };

  return (
    <Wrapper
      className={className}
      $disabled={disabled}
      $disableInput={disableInput}
      role="listbox"
      {...ariaProps}
      aria-multiselectable={false}
    >
      <ClickOutside
        onClickOutside={handleClickOutside}
        onKeyDown={handleKeyDown}
      >
        <Input
          id={dropdownId}
          data-test-id={dataTestId}
          disabled={disabled}
          label={label}
          helper={helper}
          error={error}
          value={displayValue}
          placeholder={placeholder}
          inputMode={disableInput ? "none" : undefined}
          onChange={(e) => {
            if (disableInput !== true) {
              handleInputOnChange(e);
            }
          }}
          onClick={handleInputClick}
          style={{ width: "100%" }}
          autoComplete={autoComplete}
          suffixNode={renderSuffixNode()}
          ref={targetRef}
          borderless={borderless}
          spellCheck={!disableInput}
          aria-label={ariaLabel}
          onKeyDown={handleInputKeyDown}
          data-hj-allow={dataHjAllow}
        />
        {open ? (
          <DropdownWrapper
            data-test-id="dropdown_wrapper"
            $showErrorMessage={showErrorMessage}
            $hasHelper={!!helper}
            $isOpen={open}
            $isUp={calculateIsUp()}
          >
            {filteredOptions.map((option, index) => {
              const label = getLabel(option);
              const value = getValue(option);
              const isSelected = index === highlightIndex;
              const isCustom = isCustomOption(option);

              return (
                <OptionRow
                  key={`${label} ${index}`}
                  $highlight={isSelected}
                  align="center"
                  onMouseOver={onHighlight(index)}
                >
                  <DropdownItem
                    role="option"
                    ref={(el: HTMLButtonElement | null) => {
                      if (!el) {
                        return;
                      }
                      refs.current[index] = el;
                    }}
                    tabIndex={-1}
                    $isCustom={isCustom && option.hasEdited}
                    onClick={
                      isCustom
                        ? option.onClick ??
                          (option.hasEdited
                            ? (event) => handleSelectDropDownItem(event, index)
                            : handleEditCustomOption)
                        : undefined
                    }
                    onMouseDown={
                      isCustom
                        ? undefined
                        : (event) => handleSelectDropDownItem(event, index)
                    }
                    onMouseOver={onHighlight(index)}
                    data-test-id={`dropdownItem_${value}`}
                    type="button"
                    {...(isSelected ? { "aria-selected": true } : {})}
                    disabled={option.disabled}
                  >
                    {label}
                  </DropdownItem>
                  {isCustom && option.hasEdited ? (
                    <EditOptionButton
                      onClick={handleEditCustomOption}
                      onMouseOver={onHighlight(index)}
                      data-test-id={`custom_dropdownItem_${value}`}
                      type="button"
                      {...(isSelected ? { "aria-selected": true } : {})}
                    >
                      <CustomEditSpan>
                        <StrictLocalMsg
                          defaultMessage="Edit"
                          description={{
                            text: "A button named Edit the let user know they can edit an option.",
                            img: "https://cdn.faire.com/fastly/e918de51aa45bab72dc3117b6e2bfad3a224a44af020ff47a19466fcaf91b401.png",
                          }}
                        />
                      </CustomEditSpan>
                    </EditOptionButton>
                  ) : null}
                </OptionRow>
              );
            })}

            {renderCreateNewOptionButton()}
          </DropdownWrapper>
        ) : null}
      </ClickOutside>
      {renderClearButton()}
    </Wrapper>
  );
};

Dropdown.displayName = "Dropdown";
